import { useCallback, useRef, useState } from "react";
import MarketScanner from "./MarketScanner";
import HowToPlay from "./HowToPlay";
import { useAutoRun, AutoRunBar } from "./AutoRun";
import { useServerBurst, ServerBurstPanel } from "./ServerBurst";
import { StageFX, CameraFlashes } from "./CasinoFX";
import { sfxBounce, sfxSwish, sfxClang } from "@/lib/arcadeSound";
import "./arcade-anim.css";

const PLAYER = { ready: "/arcade/bball_ready.png", shoot: "/arcade/bball_shoot.png" };

/* 🏀 glossy ball — pure SVG */
const Ball = ({ spin }) => (
  <svg viewBox="0 0 40 40" className="w-8 h-8" style={spin ? { animation: "bb2-spin .5s linear infinite" } : undefined}>
    <defs>
      <radialGradient id="bb-sheen" cx="0.35" cy="0.3" r="0.9">
        <stop offset="0" stopColor="#ffb366" />
        <stop offset="0.55" stopColor="#e2711d" />
        <stop offset="1" stopColor="#7c3a0a" />
      </radialGradient>
    </defs>
    <circle cx="20" cy="20" r="18" fill="url(#bb-sheen)" stroke="#3b1d05" strokeWidth="1.5" />
    <path d="M 2 20 Q 20 14 38 20 M 20 2 Q 26 20 20 38 M 6 8 Q 20 20 34 32" fill="none" stroke="#3b1d05" strokeWidth="1.4" />
    <ellipse cx="13" cy="11" rx="6" ry="4" fill="#fff" opacity="0.25" />
  </svg>
);

/* 🏀 Basketball Shot — a real player takes a real jump shot. ONE 1-tick
   Rise/Fall contract decides it: the market ROSE → the ball drops IN,
   it FELL → rim out. The animation always matches the settled tick. */
const BasketballShot = ({ runContract, busy, baseStake = 0.35, logExternal }) => {
  const [phase, setPhase] = useState("idle"); // idle | shot | done
  const [flight, setFlight] = useState(null); // "in" | "miss"
  const [res, setRes] = useState(null);
  const [tally, setTally] = useState({ w: 0, l: 0, net: 0 });
  const [mkt, setMkt] = useState(null);
  const symRef = useRef("1HZ100V");
  const onScan = useCallback((row) => { symRef.current = row.sym; setMkt(row); }, []);
  const auto = useAutoRun("bball");
  // 🎬 while a server season runs and you're WATCHING, every server shot
  // replays live on the court and lands in the session history
  const replayServerShot = useCallback((fresh, stB) => {
    fresh.forEach((r) => logExternal?.({
      label: "Basketball Shot", won: r.won, profit: r.profit, stake: r.stake,
      digit: r.digit, side: stB.side, sym: stB.symbol,
    }));
    const r = fresh[fresh.length - 1];
    const fw = fresh.filter((x) => x.won).length;
    const fl = fresh.length - fw;
    const fnet = fresh.reduce((a, x) => a + x.profit, 0);
    const dir = stB.side === "RISE" ? "rise" : "fall";
    setRes(null); setFlight(null); setPhase("shot"); sfxBounce();
    setTimeout(() => {
      const rose = (dir === "rise") === r.won;
      setFlight(rose ? "in" : "miss");
      if (rose) sfxSwish(); else sfxClang();
      setRes({ won: r.won, profit: r.profit, dir, rose });
      setPhase("done");
      setTally((t) => ({ w: t.w + fw, l: t.l + fl, net: Math.round((t.net + fnet) * 100) / 100 }));
      setTimeout(() => { setPhase((p) => (p === "done" ? "idle" : p)); setFlight(null); }, 1500);
    }, 950);
  }, [logExternal]);
  const srv = useServerBurst("bball", replayServerShot);
  const shooting = phase === "shot";
  const locked = busy || shooting || auto.active || srv.running;

  const shoot = async (dir) => {
    if (shooting || busy) return null;
    setRes(null); setFlight(null); setPhase("shot");
    sfxBounce();
    setTimeout(() => sfxBounce(), 300);
    const r = await runContract({
      contractType: dir === "rise" ? "CALL" : "PUT", duration: 1,
      side: dir === "rise" ? "RISE (swish)" : "FALL (miss)", label: "Basketball Shot", symbol: symRef.current,
    });
    if (!r) { setPhase("idle"); return null; }
    const rose = (dir === "rise") === r.won; // real market direction
    setFlight(rose ? "in" : "miss");
    if (rose) sfxSwish(); else sfxClang();
    setRes({ ...r, dir, rose });
    setPhase("done");
    setTally((t) => ({ w: t.w + (r.won ? 1 : 0), l: t.l + (r.won ? 0 : 1), net: Math.round((t.net + r.profit) * 100) / 100 }));
    setTimeout(() => { setPhase((p) => (p === "done" ? "idle" : p)); setFlight(null); }, 2300);
    return r;
  };

  const fire = (dir) => {
    if (auto.runs >= 50) {
      srv.start({ kind: "risefall", side: dir === "rise" ? "RISE" : "FALL", n: auto.runs,
        stake: baseStake, symbol: symRef.current, duration: 1 });
      return;
    }
    auto.burst(shoot, dir);
  };

  return (
    <div data-testid="bball-game" className="space-y-4">
      <HowToPlay id="bball"
        steps={[
          "Call the next move: 📈 RISE = the ball drops IN, 📉 FALL = the ball MISSES.",
          "Press SHOOT — your player rises for a real jump shot while one real 1-tick Rise/Fall contract fires on the scanner-locked market.",
          "The market really rose → swish through the net. It fell → rim out. The animation always matches the settled tick.",
          "Your call was right → payout ~×1.9. Wrong → the stake is lost.",
        ]}
        win="the next tick moves the way you called — rise for a swish, fall for a miss."
        payout="live Deriv Rise/Fall odds, ~×1.9 — identical to the other rise/fall games."
        tip="every shot is ~50/50 — the live flow below is history, not a prediction. Size stakes you can afford." />

      {/* 🏟 the arena */}
      <div className="relative rounded-xl border-2 border-[#F7B731]/50 overflow-hidden h-80"
        style={{ backgroundImage: "url(/arcade/bball_bg.jpeg)", backgroundSize: "cover", backgroundPosition: "center 30%", boxShadow: "inset 0 0 70px rgba(0,0,0,.65)" }}>
        <div className="absolute inset-0 bg-black/45" />
        <StageFX outcome={res && phase === "done" ? (res.won ? "win" : "loss") : null} />
        {phase === "done" && flight === "in" && <CameraFlashes />}

        {/* 🏀 hoop assembly — backboard, rim, net (animations align to THIS rim) */}
        <div className="absolute" style={{ left: "60%", top: "10%" }}>
          <div className="relative">
            {/* pole from the ceiling */}
            <div className="absolute left-1/2 -translate-x-1/2 -top-10 w-1.5 h-10 bg-white/20" />
            {/* backboard */}
            <div className="w-28 h-[72px] rounded-sm border-2 border-white/75 bg-white/10 backdrop-blur-[1px]"
              style={{ boxShadow: "0 0 16px rgba(255,255,255,.25)" }}>
              <div className="absolute left-1/2 -translate-x-1/2 bottom-1.5 w-10 h-8 border-2 border-white/60 rounded-sm" />
            </div>
            {/* rim */}
            <div className={`absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-14 h-2 rounded-full bg-[#ff6a00] border border-[#a33a00] ${phase === "done" && flight === "miss" ? "bb2-rimshake" : ""}`}
              style={{ boxShadow: "0 0 12px rgba(255,106,0,.55)" }} />
            {/* net */}
            <svg viewBox="0 0 48 40" className={`absolute left-1/2 -translate-x-1/2 -bottom-[50px] w-14 h-12 ${phase === "done" && flight === "in" ? "bb2-netsway" : ""}`} style={{ transformOrigin: "top center" }}>
              <path d="M4 2 L10 38 M14 2 L17 38 M24 2 L24 38 M34 2 L31 38 M44 2 L38 38 M4 2 Q24 10 44 2 M7 14 Q24 22 41 14 M9 26 Q24 33 39 26"
                fill="none" stroke="rgba(255,255,255,.75)" strokeWidth="1.3" />
            </svg>
          </div>
        </div>

        {/* 🧍 the player — jumps on every shot */}
        <div className="absolute" style={{ left: "10%", bottom: "4%" }}>
          <div style={{ animation: shooting || phase === "done" ? "bb2-jump 1.1s cubic-bezier(.3,.8,.4,1) forwards" : "none" }}>
            <img src={shooting || phase === "done" ? PLAYER.shoot : PLAYER.ready} alt="basketball player"
              className="h-44 w-auto max-w-none"
              style={{ filter: "drop-shadow(0 8px 14px rgba(0,0,0,.7))" }} />
          </div>
        </div>

        {/* 🏀 ball flight — launches from the player's hands to THIS rim */}
        {phase !== "idle" && (
          <div className={`absolute z-10 ${shooting ? "bb2-arc" : phase === "done" && flight === "in" ? "bb2-swish" : phase === "done" && flight === "miss" ? "bb2-rimout" : ""}`}>
            <Ball spin />
          </div>
        )}

        <div className="absolute bottom-1.5 left-2 right-2 z-10">
          {shooting && <p className="text-[11px] font-mono text-[#F7B731] animate-pulse">ball in the air — the real tick is settling…</p>}
          {phase === "done" && res && (
            <div className="arc-pop">
              <p className={`text-sm font-black font-mono ${res.won ? "text-[#089981]" : "text-[#F23645]"}`}>
                {res.rose
                  ? (res.won ? "🏀 SWISH — market ROSE" : "🏀 IT DROPPED — market didn't fall")
                  : (res.won ? "🧱 BRICKED — market FELL" : "🛑 RIM OUT — market didn't rise")} {res.won ? "· YOU WIN 🏆" : "· you lose"}
              </p>
              <p className={`text-[11px] font-mono ${res.won ? "text-[#089981]" : "text-[#F23645]"}`}>
                you called {res.dir.toUpperCase()} · {res.won ? `+$${res.profit.toFixed(2)}` : `−$${Math.abs(res.profit).toFixed(2)}`}
              </p>
            </div>
          )}
          {phase === "idle" && !res && <p className="text-[10px] font-mono text-white/60">call rise or fall · press shoot</p>}
        </div>
      </div>

      {/* ⚡ signal shortcut — shoot the strongest setup */}
      {mkt?.signal && !mkt.signal.wait && (
        <button data-testid="bball-signal-shoot" disabled={locked}
          onClick={() => fire(mkt.signal.side === "RISE" ? "rise" : "fall")}
          className="w-full h-12 rounded-lg bg-[#F7B731] hover:brightness-110 text-[#0B0E14] font-black text-sm uppercase tracking-wide transition disabled:opacity-40 active:scale-[0.99]">
          ⚡ Shoot the signal — {mkt.signal.side} on {mkt.label || mkt.sym} · {mkt.signal.strength}/100
        </button>
      )}

      <div className="grid grid-cols-2 gap-3">
        <button data-testid="bball-shoot-rise" disabled={locked} onClick={() => fire("rise")}
          className="h-16 rounded-lg bg-[#089981] hover:bg-[#067a67] text-white transition disabled:opacity-40 active:scale-[0.98] flex flex-col items-center justify-center leading-tight"
          style={{ boxShadow: "0 0 18px rgba(8,153,129,.25)" }}>
          <span className="font-black text-sm uppercase">🏀 Shoot · RISE = in{mkt && mkt.risePct >= 56 ? " 🔥" : ""}</span>
          <span className="text-[10px] font-mono text-white/80">wins if the tick closes HIGHER{mkt ? ` · recent ↑ ${mkt.risePct}%` : ""}</span>
        </button>
        <button data-testid="bball-shoot-fall" disabled={locked} onClick={() => fire("fall")}
          className="h-16 rounded-lg bg-[#F23645] hover:bg-[#C92A37] text-white transition disabled:opacity-40 active:scale-[0.98] flex flex-col items-center justify-center leading-tight"
          style={{ boxShadow: "0 0 18px rgba(242,54,69,.25)" }}>
          <span className="font-black text-sm uppercase">🧱 Shoot · FALL = miss{mkt && mkt.risePct <= 44 ? " 🔥" : ""}</span>
          <span className="text-[10px] font-mono text-white/80">wins if the tick closes LOWER{mkt ? ` · recent ↓ ${100 - mkt.risePct}%` : ""}</span>
        </button>
      </div>
      <p data-testid="bball-contract-warning" className="text-center text-[9px] font-mono text-[#ff8a95]">
        ⚠ Each game is a real contract. You can lose your whole stake. Demo uses virtual funds.
      </p>

      <ServerBurstPanel srv={srv} noun="shots" />
      <AutoRunBar id="bball" auto={auto} noun="shots" />
      {auto.runs >= 50 && !srv.running && (
        <p className="text-[9px] font-mono text-[#53b9ff]">🖥 ×{auto.runs} runs ON THE VTS SERVER — it keeps shooting even if you close the app. Come back anytime for the live score.</p>
      )}

      {/* 📊 live market panel under the court — like Penalty Shootout */}
      <MarketScanner id="bball" mode="rise" onChange={onScan}
        note="↑/↓ = share of up vs down moves in the last 50 ticks · recent flow, NOT a prediction" />

      <p data-testid="bball-tally" className="text-center text-[11px] font-mono text-white/60">
        Wins <b className="text-[#089981]">{tally.w}</b> · Losses <b className="text-[#F23645]">{tally.l}</b> · Net <b className={tally.net >= 0 ? "text-[#089981]" : "text-[#F23645]"}>${tally.net.toFixed(2)}</b>
      </p>
      <p className="text-center text-[10px] font-mono text-white/40">real Deriv 1-tick Rise/Fall contracts on the locked market · every shot is ~50/50 · losses possible</p>
      <style>{`
        @keyframes bb2-spin { 0% { transform: rotate(0) } 100% { transform: rotate(360deg) } }
        @keyframes bb2-jump { 0% { transform: translateY(0) } 35% { transform: translateY(-44px) } 70% { transform: translateY(-40px) } 100% { transform: translateY(0) } }
        /* launch from the hands, arc up over the court, hover by the rim */
        @keyframes bb2-arc {
          0%   { left: 15%; bottom: 40%; }
          45%  { left: 36%; bottom: 84%; }
          75%  { left: 54%; bottom: 85%; }
          100% { left: 63%; bottom: 74%; }
        }
        .bb2-arc { animation: bb2-arc .95s cubic-bezier(.35,.7,.45,1) forwards; left: 63%; bottom: 74%; }
        /* swish — straight down through the net */
        @keyframes bb2-swish {
          0%   { left: 63%; bottom: 74%; }
          45%  { left: 64.5%; bottom: 66%; }
          100% { left: 64.5%; bottom: 46%; opacity: .9; }
        }
        .bb2-swish { animation: bb2-swish .7s cubic-bezier(.4,0,.8,.4) forwards; left: 64.5%; bottom: 46%; }
        /* rim out — clang off the iron and bounce away */
        @keyframes bb2-rimout {
          0%   { left: 63%; bottom: 74%; transform: rotate(0); }
          30%  { left: 59%; bottom: 79%; transform: rotate(-30deg); }
          65%  { left: 50%; bottom: 46%; transform: rotate(-110deg); }
          100% { left: 42%; bottom: 7%;  transform: rotate(-200deg); opacity: .85; }
        }
        .bb2-rimout { animation: bb2-rimout 1s ease-in forwards; left: 42%; bottom: 7%; }
        @keyframes bb2-netsway { 0%,100% { transform: translateX(-50%) skewX(0) } 30% { transform: translateX(-50%) skewX(8deg) } 65% { transform: translateX(-50%) skewX(-6deg) } }
        .bb2-netsway { animation: bb2-netsway .8s ease-out .35s 2; }
        @keyframes bb2-rimshake { 0%,100% { transform: translateX(-50%) } 25% { transform: translateX(calc(-50% - 3px)) } 75% { transform: translateX(calc(-50% + 3px)) } }
        .bb2-rimshake { animation: bb2-rimshake .3s linear 3; }
      `}</style>
    </div>
  );
};

export default BasketballShot;
